import * as React from 'react'
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { cn } from '@/lib/utils'

/**
 * Figma (node 466:3596 — Navigation Menu):
 *
 * Root:        flex items-center, relative z-10
 * List:        flex list-none items-center space-x-1
 * Trigger:     h-9, px-4 py-2, text-sm font-medium, rounded-md
 *              hover:bg-accent, data-[state=open]:bg-accent/50
 *              ChevronDown rotates 180° on open
 * Content:     rounded-md, border, bg-popover, shadow, p-4
 * Link item:   rounded-md, p-3, hover:bg-accent
 *   Title:     text-sm font-medium leading-none
 *   Description: text-sm text-muted-foreground leading-snug
 * Viewport:    rounded-md, border, bg-popover, shadow, mt-1.5
 */

/* ─── Root ─── */
const IntuiNavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenu>,
  React.ComponentPropsWithoutRef<typeof NavigationMenu>
>(({ className, ...props }, ref) => (
  <NavigationMenu ref={ref} className={className} {...props} />
))
IntuiNavigationMenu.displayName = 'IntuiNavigationMenu'

/* ─── List ─── */
const IntuiNavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuList>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuList>
>(({ className, ...props }, ref) => (
  <NavigationMenuList ref={ref} className={className} {...props} />
))
IntuiNavigationMenuList.displayName = 'IntuiNavigationMenuList'

/* ─── Item ─── */
const IntuiNavigationMenuItem = React.forwardRef<
  React.ElementRef<typeof NavigationMenuItem>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuItem>
>(({ ...props }, ref) => <NavigationMenuItem ref={ref} {...props} />)
IntuiNavigationMenuItem.displayName = 'IntuiNavigationMenuItem'

/* ─── Trigger ─── */
const IntuiNavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuTrigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuTrigger>
>(({ className, ...props }, ref) => (
  <NavigationMenuTrigger ref={ref} className={cn('rounded-lg', className)} {...props} />
))
IntuiNavigationMenuTrigger.displayName = 'IntuiNavigationMenuTrigger'

/* ─── Content ─── */
const IntuiNavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuContent>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuContent>
>(({ className, ...props }, ref) => (
  <NavigationMenuContent ref={ref} className={className} {...props} />
))
IntuiNavigationMenuContent.displayName = 'IntuiNavigationMenuContent'

/* ─── Link ─── */
const IntuiNavigationMenuLink = React.forwardRef<
  React.ElementRef<typeof NavigationMenuLink>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuLink>
>(({ ...props }, ref) => <NavigationMenuLink ref={ref} {...props} />)
IntuiNavigationMenuLink.displayName = 'IntuiNavigationMenuLink'

/* ─── ListItem (convenience component for link items with title + description) ─── */

export interface IntuiNavigationMenuListItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  title: string
  icon?: React.ReactNode
}

const IntuiNavigationMenuListItem = React.forwardRef<
  HTMLAnchorElement,
  IntuiNavigationMenuListItemProps
>(({ className, title, icon, children, ...props }, ref) => (
  <li>
    <NavigationMenuLink asChild>
      <a
        ref={ref}
        className={cn(
          'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:bg-accent focus:text-accent-foreground',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="flex shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>}
          <div className="text-sm font-medium leading-none">{title}</div>
        </div>
        {children && (
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        )}
      </a>
    </NavigationMenuLink>
  </li>
))
IntuiNavigationMenuListItem.displayName = 'IntuiNavigationMenuListItem'

export {
  IntuiNavigationMenu,
  IntuiNavigationMenuList,
  IntuiNavigationMenuItem,
  IntuiNavigationMenuTrigger,
  IntuiNavigationMenuContent,
  IntuiNavigationMenuLink,
  IntuiNavigationMenuListItem,
  navigationMenuTriggerStyle,
}
